import { useState, type FormEvent, type JSX } from 'react';
import { Link, useSearchParams } from 'react-router';
import { usePostHog } from '@posthog/react';
import {
  Button,
  Card,
  Field,
  PencilIcon,
  PlayIcon,
  RefreshIcon,
  SaveIcon,
  SparkleIcon,
  Textarea
} from '@viniciomendesr/rayrovisk-design-system';
import { useCredits, useCreatePin, useGenerateScript, useMe, useProfile } from '../api/queries';
import { AccessStateScreen, accessStateOf } from '../access/AccessState';
import { ApiError, grantsContent } from '../api/client';
import { ChipChoice, type ChipOption } from '../components/chips';
import { CopyToClipboard } from '../components/CopyToClipboard';
import { ScriptEditor } from '../components/ScriptEditor';
import type { ScriptFormat } from '../api/types';

/**
 * The four shapes the service can write. This is a choice per idea, not a
 * profile setting: the format used to be `platforms[0]`, the first thing she
 * ticked during onboarding, so every script came out a reel.
 */
const FORMATS: ChipOption[] = [
  { value: 'reels', label: 'Reels' },
  { value: 'stories', label: 'Stories' },
  { value: 'carousel', label: 'Carrossel' },
  { value: 'static', label: 'Post estático' }
];

/**
 * Screen 10. This is the one place in the product that calls a model, so it is
 * the one place a click costs money. Everything here exists to make that
 * visible: the balance before, the balance after, and an honest state when the
 * provider is not configured at all.
 */
export function ScriptsScreen(): JSX.Element {
  const credits = useCredits();
  const posthog = usePostHog();
  const profile = useProfile();
  const generate = useGenerateScript();
  const createPin = useCreatePin();
  const me = useMe();
  const [search] = useSearchParams();
  // Initial value only: after the first render the field is hers, and rewriting
  // the URL per keystroke would fight the history stack.
  const [task, setTask] = useState(() => search.get('tarefa') ?? '');
  const [format, setFormat] = useState<ScriptFormat>('reels');
  const [pinnedScriptId, setPinnedScriptId] = useState<string | null>(null);
  /**
   * Her edit, keyed by the script it belongs to so a new generation is never
   * shown wearing the previous one's text. In memory only: she copies or pins
   * to keep it.
   */
  const [edited, setEdited] = useState<{ scriptId: string; script: string } | null>(null);

  const state = accessStateOf(credits.error) ?? accessStateOf(generate.error);
  if (state) {
    return <AccessStateScreen state={state} />;
  }

  const generated = generate.data;
  const currentScript =
    generated && edited?.scriptId === generated.scriptId ? edited.script : generated?.script ?? '';
  const balance = credits.data?.credits.balance ?? 0;
  const granted = credits.data?.credits.granted ?? 0;
  const error = generate.error instanceof ApiError ? generate.error : null;
  const unavailable = error?.code === 'script_generation_unavailable';
  const broke = error?.code === 'no_credits' || (credits.isSuccess && balance <= 0);
  /**
   * The ten account credits are free for anyone who does not hold
   * `prompt-generator` — the same derivation the navigation badge uses. Owning
   * a smaller prompt pack changes the upgrade price, not where these credits
   * came from.
   *
   * Undefined while `/v1/me` is in flight, so nothing on screen commits to
   * either word before the answer arrives.
   */
  const onFreeCredits = me.data
    ? !grantsContent(me.data.products['prompt-generator'])
    : undefined;

  /**
   * Both endings are counted, and the failure is the one worth having: this is
   * the only capability in the product that costs money per use, so a refusal
   * here is either a missing `GEMINI_API_KEY` answering `503` or a person who
   * ran out. Measuring only the successes would show a generator that always
   * works to exactly the people it already worked for.
   */
  async function generateScript(): Promise<void> {
    try {
      const data = await generate.mutateAsync({ task, format });
      setEdited(null);
      posthog?.capture('script_generated', {
        format: data.format,
        credits_remaining: data.credits.balance
      });
      // Spending the last one is the renewal conversation, and it happens on a
      // screen that otherwise looks like a success.
      if (data.credits.balance === 0) {
        posthog?.capture('credits_exhausted', { granted: data.credits.granted, refused: false });
      }
    } catch (failure) {
      const code = failure instanceof ApiError ? failure.code : 'unknown';
      posthog?.capture('script_generation_failed', { code });
      if (code === 'no_credits') {
        posthog?.capture('credits_exhausted', {
          granted: credits.data?.credits.granted ?? 0,
          refused: true
        });
      }
    }
  }

  return (
    <section className="rvk-split" style={{ maxWidth: 1180 }}>
      <div className="rvk-split-main" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <h1 style={{ margin: 0, font: '400 32px/1.12 var(--rvk-font-expressive)' }}>
            Gerador de Roteiros
          </h1>
          <p
            style={{
              margin: 0,
              font: '400 14.5px/1.6 var(--rvk-font-functional)',
              color: 'var(--rvk-text-secondary)'
            }}
          >
            Aqui o roteiro sai escrito, não o prompt. Você conta a ideia do vídeo e recebe gancho,
            blocos de fala e fechamento adaptados ao contexto do seu perfil. Cada roteiro gasta 1 crédito.
          </p>
        </div>

        {unavailable && (
          <Card layer="functional" padding="lg">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={{ font: '400 19px/1.2 var(--rvk-font-expressive)' }}>
                O gerador de roteiros ainda está em preparação.
              </span>
              <span
                style={{
                  font: '400 13.5px/1.5 var(--rvk-font-functional)',
                  color: 'var(--rvk-text-secondary)'
                }}
              >
                Nenhum crédito foi usado. Assim que ele abrir, os seus {granted} créditos continuam
                lá, do mesmo jeito.
              </span>
            </div>
          </Card>
        )}

        <Card layer="functional" padding="lg">
          <form
            onSubmit={(event: FormEvent) => {
              event.preventDefault();
              void generateScript();
            }}
            style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
          >
            <ChipChoice
              legend="O que você vai publicar"
              hint="Cada formato sai com uma estrutura própria."
              choices={FORMATS}
              value={format}
              onChange={(value) => setFormat(value as ScriptFormat)}
              clearable={false}
            />
            <Field
              label="Conte a ideia do vídeo"
              hint="Como você contaria para uma amiga. Sem precisar dizer o formato: ele está marcado aí em cima."
            >
              <Textarea rows={3} autoGrow value={task} onChange={(event) => setTask(event.target.value)} />
            </Field>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <Button
                type="submit"
                variant="primary"
                loading={generate.isPending}
                iconStart={<SparkleIcon size={14} />}
                access={broke ? 'unavailable' : undefined}
                accessReason="Você está sem créditos de roteiro."
              >
                Gerar roteiro · 1 crédito
              </Button>
              <span
                style={{
                  font: '400 12.5px/1.4 var(--rvk-font-functional)',
                  color: 'var(--rvk-text-secondary)'
                }}
              >
                {balance} de {granted} créditos
              </span>
            </div>
          </form>
        </Card>

        {error && !unavailable && !broke && (
          <p style={{ margin: 0, font: '400 13.5px/1.5 var(--rvk-font-functional)' }}>{error.message}</p>
        )}

        {generate.data && (
          // Keyed by the script text: each new version enters, a re-render of
          // the same one does not.
          <Card
            key={generate.data.script}
            className="rvk-rise"
            layer="functional"
            elevation="raised"
            padding="lg"
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  flexWrap: 'wrap'
                }}
              >
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    font: '600 10px/1 var(--rvk-font-functional)',
                    letterSpacing: '.13em',
                    textTransform: 'uppercase',
                    color: 'var(--rvk-text-tertiary)'
                  }}
                >
                  <PlayIcon size={13} /> Roteiro pronto
                </span>
                <span
                  style={{
                    font: '400 12.5px/1.4 var(--rvk-font-functional)',
                    color: 'var(--rvk-text-secondary)'
                  }}
                >
                  {generate.data.format} · 1 crédito usado
                </span>
              </div>
              {/* Copiar e fixar levam o texto que está na tela, editado ou
                  não. Guardar o do modelo depois de ela reescrever seria
                  entregar uma versão que ninguém aprovou. */}
              <ScriptEditor
                original={generate.data.script}
                value={currentScript}
                onChange={(script) => setEdited({ scriptId: generate.data!.scriptId, script })}
                actions={
                  <>
                    <CopyToClipboard
                      text={currentScript}
                      surface="script"
                      sourceRef={generate.data.scriptId}
                      label="Copiar roteiro"
                      variant="secondary"
                    />
                    <Button
                      variant="secondary"
                      loading={createPin.isPending}
                      iconStart={<SaveIcon size={14} />}
                      onClick={() => {
                        const data = generate.data;
                        if (!data) return;
                        void createPin
                          .mutateAsync({
                            kind: 'script',
                            sourceRef: data.scriptId,
                            label: task,
                            body: currentScript,
                            format: data.format
                          })
                          .then(() => {
                            setPinnedScriptId(data.scriptId);
                            posthog?.capture('script_pinned', {
                              format: data.format,
                              edited: currentScript !== data.script
                            });
                          })
                          .catch(() => undefined);
                      }}
                    >
                      {pinnedScriptId === generate.data.scriptId ? 'Fixado' : 'Fixar'}
                    </Button>
                    <Button
                      variant="ghost"
                      iconStart={<RefreshIcon size={14} />}
                      loading={generate.isPending}
                      access={balance <= 0 ? 'unavailable' : undefined}
                      accessReason="Você está sem créditos de roteiro."
                      onClick={() => void generateScript()}
                    >
                      Gerar outra versão · 1 crédito
                    </Button>
                  </>
                }
              />
              {createPin.isError && (
                <p style={{ margin: 0, font: '400 12.5px/1.4 var(--rvk-font-functional)' }}>
                  {(createPin.error as Error).message}
                </p>
              )}
            </div>
          </Card>
        )}
      </div>

      <div className="rvk-split-aside" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Card layer="functional" padding="md" title="Seus créditos">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 4 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ font: '400 34px/1 var(--rvk-font-expressive)' }}>{balance}</span>
              <span
                style={{
                  font: '400 12.5px/1.4 var(--rvk-font-functional)',
                  color: 'var(--rvk-text-secondary)'
                }}
              >
                {/* She did not buy the trial's ten, and this is the one place
                    the product counts something she owns. "Comprados" there is
                    a small lie in the worst possible spot — and a demonstration
                    that pretends to be a purchase has nothing left to sell. */}
                de {granted} {onFreeCredits ? 'grátis' : 'comprados'}
              </span>
            </div>
            <div style={{ height: 6, borderRadius: 999, background: 'var(--rvk-surface-2)' }}>
              <div
                style={{
                  height: 6,
                  borderRadius: 999,
                  width: `${granted === 0 ? 0 : Math.round((balance / granted) * 100)}%`,
                  background: 'var(--rvk-accent-default)'
                }}
              />
            </div>
            <span
              style={{
                font: '400 12px/1.5 var(--rvk-font-functional)',
                color: 'var(--rvk-text-tertiary)'
              }}
            >
              {onFreeCredits
                ? 'Estes são os seus 10 créditos gratuitos para testar o gerador. Cada roteiro usa 1 crédito, e eles não expiram.'
                : '1 crédito por roteiro gerado. Os créditos não expiram.'}
            </span>
            {/* Two offers. Someone on the free credits is sent to the product;
                a generator buyer waits for the top-up that does not exist yet.
                The third was the R$ 197 upgrade for a lower-product buyer, and
                it retired with the generator's own price: nobody pays 197 for
                what the public page sells for 37. */}
            {onFreeCredits ? (
              <Link to="/">
                <Button variant="secondary" size="sm" fullWidth>
                  Conhecer o Gerador · 100 créditos
                </Button>
              </Link>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                fullWidth
                access="unavailable"
                accessReason="A recarga de créditos entra depois do lançamento."
              >
                Recarregar créditos
              </Button>
            )}
          </div>
        </Card>

        <Card layer="functional" padding="md" title="Seu perfil" subtitle="O contexto usado em cada roteiro.">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 4 }}>
            <span style={{ font: '400 13px/1.45 var(--rvk-font-functional)' }}>
              {profile.data?.profile?.identity.niche ?? 'Perfil ainda não preenchido'}
            </span>
            <Link to="/gerador/perfil">
              <Button variant="ghost" size="sm" fullWidth iconStart={<PencilIcon size={14} />}>
                Editar perfil
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    </section>
  );
}
